// PWA — 任务结果查看组件（Req 7.5, 8.1, 8.2 / 任务 9.5）
//
// 展示最近一次提交任务的轮询进度与结果：
//  - queued / running：显示中文状态与轮询骨架（Req 7.5）；
//  - success：用 presigned URL 直取 S3 原图，以「适配分辨率」显示（Req 8.1, 8.2）；
//  - failed：显示可读失败原因。
//
// 本组件为展示型：轮询状态由上层 useJobPolling Hook 提供（见 App.tsx），
// 与提交表单共享同一 authedFetch。
//
// 结构上为任务 9.6（保存图片 / 取图失败重试）预留位置：每张图片包裹在
// `.job-viewer__figure` 中，后续可在其内追加保存 / 重试操作而不改动轮询逻辑。

import { useCallback, useEffect, useState } from "react";
import type { PresignedImage } from "@comfy/shared";
import type { UseJobPollingResult } from "./useJobPolling.js";
import {
  canShareImageFiles,
  decideRetryStrategy,
  deriveDownloadName,
  deriveJobView,
  fitToViewport,
  hasWebShare,
  withCacheBust,
} from "./jobView.js";

export interface JobViewerProps {
  /** useJobPolling 的返回值（由上层 App 持有并下传）。 */
  job: UseJobPollingResult;
  /** 是否已认证：未认证时不展示。 */
  authenticated: boolean;
}

export function JobViewer({
  job,
  authenticated,
}: JobViewerProps): JSX.Element | null {
  const { job: response, error, refresh } = job;
  const view = deriveJobView(response);

  // 未认证或尚无任务：不渲染本卡片（提交成功后由上层设置 jobId 触发展示）。
  if (!authenticated || !view) {
    return null;
  }

  return (
    <section className="card" aria-label="生成结果">
      <h2 className="card__title">生成结果</h2>

      <div className="job-viewer__status" role="status" aria-live="polite">
        <span className={`job-pill job-pill--${view.status}`}>
          {view.label}
        </span>
        {view.polling && <span className="app__hint">正在轮询任务状态…</span>}
      </div>

      {/* 轮询进行中（queued / running）：骨架占位。 */}
      {view.polling && (
        <div className="job-viewer__pending">
          <div className="skeleton skeleton--image" aria-hidden="true" />
        </div>
      )}

      {/* 成功：直取 presigned 原图并适配显示（Req 8.1, 8.2）；
          每张图片提供保存 / 分享与取图失败重试（Req 8.3, 8.4，任务 9.6）。 */}
      {view.status === "success" && view.images.length > 0 && (
        <div className="job-viewer__images">
          {view.images.map((img, i) => (
            <JobImage
              key={`${i}-${img.url}`}
              image={img}
              index={i}
              onRefresh={refresh}
            />
          ))}
        </div>
      )}

      {/* 成功但无图片（异常情形）。 */}
      {view.status === "success" && view.images.length === 0 && (
        <p className="app__hint" role="status">
          任务已完成，但未返回图片。
        </p>
      )}

      {/* 失败：显示可读原因（Req 11.3 展示端）。 */}
      {view.error && (
        <p className="app__hint" role="alert" style={{ textAlign: "left" }}>
          {view.error}
        </p>
      )}

      {/* 轮询自身的网络错误（区别于任务 failed）。 */}
      {error && (
        <p className="app__hint" role="alert" style={{ textAlign: "left" }}>
          {error}
        </p>
      )}
    </section>
  );
}

interface JobImageProps {
  /** 成功任务的一张 presigned 图片（Req 8.1）。 */
  image: PresignedImage;
  /** 图片序号（用于回落文件名与 alt 文案）。 */
  index: number;
  /** 重取任务以刷新 presigned URL（取图失败重试路径，Req 8.4）。 */
  onRefresh: () => void;
}

// 单张图片：负责「适配分辨率显示 + 保存 / 分享 + 取图失败重试」（任务 9.6）。
// 采用子组件以持有各图片独立的加载失败 / 重试状态，避免污染父级轮询逻辑。
function JobImage({ image, index, onRefresh }: JobImageProps): JSX.Element {
  const filename = deriveDownloadName(image.url, index);

  // 取图失败与重试状态（Req 8.4）。
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [errored, setErrored] = useState(false);
  // 缓存破坏计数：递增以强制 <img> 重新加载同一 URL。
  const [bust, setBust] = useState(0);
  // 分享（Web Share API）过程中的状态与错误（Req 8.3）。
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  // presigned URL 变化（refresh 拿到新 URL）时复位失败状态并重载。
  useEffect(() => {
    setErrored(false);
    setBust(0);
    setFailedAttempts(0);
  }, [image.url]);

  // 展示用 URL：附加缓存破坏参数以支持重试重载（bust=0 时为原始 URL）。
  const displaySrc = bust > 0 ? withCacheBust(image.url, bust) : image.url;

  // 图片加载后按视口「适配分辨率」显示（Req 8.2）：保持宽高比、不放大。
  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      const natural = { width: img.naturalWidth, height: img.naturalHeight };
      // 视口：容器可用宽（屏宽 - 单列内边距），高度取屏高的 70% 作上限。
      const viewport = {
        width: Math.max(0, window.innerWidth - 64),
        height: Math.max(0, window.innerHeight * 0.7),
      };
      const fit = fitToViewport(natural, viewport);
      if (fit.width > 0 && fit.height > 0) {
        img.style.width = `${fit.width}px`;
        img.style.height = `${fit.height}px`;
      }
    },
    [],
  );

  // <img> 加载失败：记一次失败并进入错误态（Req 8.4）。
  const onImageError = useCallback(() => {
    setErrored(true);
    setFailedAttempts((n) => n + 1);
  }, []);

  // 重试：首次失败重载原图；再次失败先刷新 presigned URL 再重载（Req 8.4）。
  const onRetry = useCallback(() => {
    const strategy = decideRetryStrategy(failedAttempts);
    setErrored(false);
    if (strategy === "refresh") {
      // presigned URL 可能已过期 → 重取任务拿新 URL（useEffect 会随之复位）。
      onRefresh();
    } else {
      // 瞬时失败 → 附加缓存破坏参数强制重载同一 URL。
      setBust((n) => n + 1);
    }
  }, [failedAttempts, onRefresh]);

  // 分享 / 保存到系统相册（Req 8.3）：取原图 → 构造 File → Web Share API。
  // 不可用时静默回落到下方 <a download>（长按亦可保存）。
  const onShare = useCallback(async () => {
    setShareError(null);
    setSharing(true);
    try {
      const res = await fetch(image.url);
      if (!res.ok) {
        throw new Error(`取图失败（${res.status}）`);
      }
      const blob = await res.blob();
      const file = new File([blob], filename, {
        type: blob.type || "image/png",
      });
      const nav = typeof navigator !== "undefined" ? navigator : undefined;
      if (canShareImageFiles(nav, [file])) {
        await nav!.share({ files: [file], title: filename });
      } else {
        // 能力不足：提示改用「保存原图」链接（长按 / 下载）。
        setShareError("此设备不支持系统分享，请使用「保存原图」。");
      }
    } catch (err: unknown) {
      // 用户取消分享（AbortError）不视为错误。
      if (err instanceof DOMException && err.name === "AbortError") {
        // 忽略取消。
      } else {
        setShareError(
          err instanceof Error ? `分享失败：${err.message}` : "分享失败",
        );
      }
    } finally {
      setSharing(false);
    }
  }, [image.url, filename]);

  // 特征检测：仅在支持 Web Share 时展示分享按钮，桌面浏览器回落到下载链接。
  const showShare =
    typeof navigator !== "undefined" && hasWebShare(navigator);

  return (
    <figure className="job-viewer__figure">
      {errored ? (
        // 取图失败：显示错误信息与重试按钮（Req 8.4）。
        <div className="job-viewer__error" role="alert">
          <p className="app__hint" style={{ textAlign: "left" }}>
            图片加载失败，请重试。
          </p>
          <button type="button" className="btn" onClick={onRetry}>
            重试
          </button>
        </div>
      ) : (
        <>
          <img
            className="job-viewer__image"
            src={displaySrc}
            alt={`生成图片 ${index + 1}`}
            onLoad={onImageLoad}
            onError={onImageError}
          />
          {/* 保存 / 分享操作（Req 8.3）。 */}
          <div className="job-viewer__actions">
            {/* 原图下载链接：长按亦可保存到相册；桌面点击下载。 */}
            <a
              className="btn"
              href={image.url}
              download={filename}
              target="_blank"
              rel="noreferrer"
            >
              保存原图
            </a>
            {showShare && (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void onShare()}
                disabled={sharing}
              >
                {sharing ? "分享中…" : "分享到相册"}
              </button>
            )}
          </div>
          {shareError && (
            <p
              className="app__hint"
              role="alert"
              style={{ textAlign: "left" }}
            >
              {shareError}
            </p>
          )}
        </>
      )}
    </figure>
  );
}
